import {formatDate, formatTime, parseInstant, Temporal} from "@/shared/utils/datefunctions";
import {WorkingTimeRequestStepStatus} from "@/shared/timrTypes";
import {WorkingTimeRequestStep} from "@/shared/timr";
import {htmlEscape} from "@/shared/utils/string";

export class WorkingTimeRequestStepsComponent extends HTMLElement {

	private _steps: WorkingTimeRequestStep[] = [];

	public set steps(config: WorkingTimeRequestStep[]) {
		this._steps = config;
		this.render();
	}

	private render() {
		this.replaceChildren();
		for (const step of this._steps) {
			const stepElement = document.createElement('div');
			stepElement.setAttribute('step', step.status ?? "");

			const icon = document.createElement('i');
			icon.classList.add('far');
			stepElement.appendChild(icon);
			stepElement.appendChild(document.createTextNode(`${htmlEscape(step.requiredApproverUser?.fullname) ?? messages["workingTimeRequestStepConfigurationType." + step.requiredApproverType]}`));

			$(stepElement).popover({
				trigger: 'hover',
				placement: 'bottom',
				container: ".content-wrapper",
				delay: {show: 500, hide: 0},
				html: true,
				template: `<div class="popover" style="min-width: 300px;" role="tooltip"><div class="arrow"></div><div class="popover-content p-15 working-time-request-step-popup"></div></div>`,
				content: this.buildPopoverContent(step)
			});

			this.appendChild(stepElement);
		}
	}

	private buildPopoverContent(step: WorkingTimeRequestStep): string {
		const dateRow = this.buildDateRow(step);
		const proofReviewActionRow = this.buildProofReviewActionRow(step);
		const commentRow = this.buildCommentRow(step);
		return `
			<div class="working-time-request-step-popup">
				${this.buildLabelAndBadgeRow(step)}
				${this.buildOverruledInfoRow(step)}
				${dateRow || proofReviewActionRow ? `<hr class="popup-separator" />` : ''}
				${dateRow}
				${proofReviewActionRow}
				${commentRow ? `<hr class="popup-separator" />` : ''}
				${commentRow}
			</div>
			`;
	}

	private buildLabelAndBadgeRow(step: WorkingTimeRequestStep) {
		const status = step.status;

		// Make use of JS short-circuit evaluation to determine the username:
		// 1. If there's a required approver user, use their full name.
		// 2. If the step is in a finished state (approved, rejected, overruled), use the modifiedByUser's full name.
		// 3. If neither is available, the result will be undefined or null, and we will fall back to using the approver type as label.
		// This way, we ensure that we always have a meaningful label to display.
		const userName = step.completedByUser?.fullname || step.requiredApproverUser?.fullname;
		const label = userName || messages["workingTimeRequestStepConfigurationType." + step.requiredApproverType];

		return `
			<div class="popup-header">
				<div class="popup-label">
					<span>${userName ? `<i class="far fa-user position-left"></i>` : ""} ${htmlEscape(label)}</span>
				</div>
				<div>
					<div class="status-badge status-${status}">
						<div class="flex-row flex-valign-middle">
							<i class="status-icon"></i>
							<span class="status-text">${messages["workingTimeRequestStepStatus." + status]}</span>
						</div>
					</div>
				</div>
			</div>
			`;
	}

	private buildOverruledInfoRow(step: WorkingTimeRequestStep) {
		if (![WorkingTimeRequestStepStatus.OVERRULED_APPROVED, WorkingTimeRequestStepStatus.OVERRULED_REJECTED].includes(step.status)) {
			return '';
		}
		return `
			<div class="popup-overruled-info">
				${messages.withArguments(
			"workingTimeRequestStepsComponent.intended",
			htmlEscape(step.requiredApproverUser?.fullname) || messages["workingTimeRequestStepConfigurationType." + step.requiredApproverType])
		}
			</div>
			`;
	}

	private buildDateRow(step: WorkingTimeRequestStep) {
		if (!step.completedAt) {
			return '';
		}
		const completedAt = parseInstant(step.completedAt)?.toZonedDateTimeISO(Temporal.Now.timeZoneId());
		return `
			<div class="date-time-container">
				<div class="flex-column-30">
					<span>${messages["workingTimeRequestStepsComponent.time"]}</span>
				</div>
				<div class="flex-column date-time">
					<span>${formatDate(completedAt)}</span>
					<span class="time">${formatTime(completedAt)}</span>
				</div>
			</div>
			`;
	}

	private buildProofReviewActionRow(step: WorkingTimeRequestStep) {
		if (!step.proofReviewAction) {
			return '';
		}
		return `
			<div class="flex-row mt-10">
				<div class="flex-column-30">
					<span>${messages["workingTimeRequestStepsComponent.proof"]}</span>
				</div>
				<div class="flex-column">
					<span class="label proof-review-action status-${step.proofReviewAction}">
						<span>${messages["proofReviewAction." + step.proofReviewAction]}</span>
					</span>
				</div>
			</div>
			`;
	}

	private buildCommentRow(step: WorkingTimeRequestStep) {
		if (!step.comment) {
			return '';
		}
		return `
			<div class="popup-reason">
				<div class="reason-label">${messages["workingTimeRequest.statusComment"]}</div>
				<div class="reason-text">${htmlEscape(step.comment)}</div>
			</div>
			`;
	}
}
